/**
 * Stateless builders that turn a compute/recompute response's `result` into the
 * `data`-shaped object each self-service chart component consumes. The DVM
 * layout builder produces these same shapes for its preview tiles + published
 * layout_json; the PUBLISHED dashboard view reuses them here to live-update a
 * target chart when an input control (slider / input box) changes, without a
 * full reload.
 *
 * These are intentionally free of component state — every label / id the shape
 * needs is passed in — so both the builder and the published view can call them.
 */

import { formatNumber } from './number-format.utils';

export type RecomputeChartType =
  | 'WATERFALL'
  | 'DRILL_DOWN_TABLE'
  | 'HORIZONTAL_BAR_CHART'
  | 'KPI'
  | 'INPUT';

interface WaterfallBarLike {
  label: string;
  delta: number;
  bottom: number;
  top: number;
  type?: string;

  colorSign?: string;
}

/** Build the ECharts option for a waterfall bridge from its computed bars. */
function toAxisLabelRotation(value: unknown, fallback: number): number {
  const rotation = Number(value ?? fallback);
  return Number.isFinite(rotation) ? rotation : fallback;
}

/**
 * Attaches each step's configured "Direction" to its bar as `colorSign` — a
 * display-only hint `colorFor` (in buildWaterfallOption) can use instead of
 * the bar's own delta sign. Deliberately does NOT touch delta/bottom/top:
 * those are the backend's own computed bridge values, and "Direction" must
 * only ever choose a color, never recompute the numbers or bridge position
 * it's painted on.
 */
function applyWaterfallStepSigns(
  bars: WaterfallBarLike[],
  payload?: Record<string, unknown>,
): WaterfallBarLike[] {
  if (!Array.isArray(bars) || !bars.length) return [];
  const steps = Array.isArray(payload?.['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
  if (!steps.length) return bars;

  const normalize = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const stepsByLabel = new Map<string, Record<string, unknown>>();
  steps.forEach((step) => {
    const key = normalize(step['label']);
    if (key) stepsByLabel.set(key, step);
  });

  return bars.map((bar, index) => {
    const matchingStep = stepsByLabel.get(normalize(bar.label)) ?? steps[index];
    const sign = String(matchingStep?.['sign'] ?? 'AUTO').toUpperCase();
    return sign === 'AUTO' ? bar : { ...bar, colorSign: sign };
  });
}

export function buildWaterfallOption(
  bars: WaterfallBarLike[],
  format?: Record<string, unknown>,
): Record<string, unknown> {
  const categories = bars.map((bar) => bar.label);

  const positiveColor = (format?.['positiveColor'] as string) || '#10b981';
  const negativeColor = (format?.['negativeColor'] as string) || '#ef4444';
  const totalColor = (format?.['totalColor'] as string) || '#1f6feb';
  const showValues = format?.['showValues'] !== false;
  const numberFormat = (format?.['numberFormat'] as string) || 'NONE';
  const decimalsRaw = format?.['decimals'];
  const decimals = decimalsRaw !== undefined && decimalsRaw !== null && decimalsRaw !== '' ? Number(decimalsRaw) : undefined;
  // An explicit Number-scale choice (Formatting accordion) overrides the preset's
  // own scaling by mapping onto the equivalent preset; 'none' keeps the preset.
  const numberScale = String(format?.['numberScale'] ?? 'none');
  const scaleToPreset: Record<string, string> = {
    compact: 'COMPACT',
    thousands: 'SCALE_THOUSANDS',
    millions: 'MILLIONS',
    billions: 'BILLIONS',
  };
  const effectiveFormat = scaleToPreset[numberScale] ?? numberFormat;
  const xAxisLabelRotation = toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
  // Wrap long step labels (e.g. "Marketing Program Discounts") onto a second
  // line instead of overlapping — configurable chars-per-line, 0 (or unset)
  // disables wrapping, showing the full label on one line.
  const xAxisLabelMaxChars = Math.max(0, Number(format?.['xAxisLabelMaxChars'] ?? 14) || 0);
  const wrapLabel = (value: string): string => {
    if (!xAxisLabelMaxChars || value.length <= xAxisLabelMaxChars) return value;
    // Break at the last space at/before the limit (keeps whole words together);
    // no space found (one long word) → hard-break at the limit instead.
    const breakAt = value.lastIndexOf(' ', xAxisLabelMaxChars);
    const splitAt = breakAt > 0 ? breakAt : xAxisLabelMaxChars;
    const firstLine = value.slice(0, splitAt).trim();
    const secondLine = value.slice(breakAt > 0 ? splitAt + 1 : splitAt).trim();
    return `${firstLine}\n${secondLine}`;
  };

  /** Where a bar sits and how tall it is.
   *
   *  Position came from the backend's `bottom`/`top` while height came from
   *  `|delta|` — and those two can DISAGREE, but only when the step's OWN
   *  declared sign contradicts what delta itself computed to. A cost step
   *  tagged NEGATIVE whose value enters the bridge as a POSITIVE amount gets
   *  an ascending pair (e.g. 300 → 507) even though it's meant to descend, so
   *  "Total Cost -207K" climbed 207K instead of dropping to the next total -
   *  THAT mismatch is what needs correcting.
   *
   *  A step whose delta sign already AGREES with its declared sign (Marketing
   *  Discount: tagged NEGATIVE, delta genuinely negative) has bottom/top that
   *  ALREADY correctly span |delta| in the right direction - top>bottom is
   *  true for it too, so checking that alone (without also checking delta's
   *  own sign) wrongly re-derives its base and shifts an already-correct bar
   *  down by its own height a second time.
   *
   *  TOTAL/START bars are absolute (measured from the axis, not relative to a
   *  neighbour) so they're never second-guessed; a bar with no declared sign
   *  at all has nothing to disagree with delta, so it's drawn as sent too. */
  const geometryFor = (bar: WaterfallBarLike): { base: number; height: number } => {
    const base = Math.min(bar.bottom, bar.top);
    const height = Math.abs(bar.delta);
    const type = (bar.type || '').toUpperCase();
    if (type === 'TOTAL' || type === 'START') return { base, height };
    const declaredNegative = bar.colorSign === 'NEGATIVE';
    const declaredPositive = bar.colorSign === 'POSITIVE';
    const deltaNegative = bar.delta < 0;
    if (declaredNegative && !deltaNegative && bar.top > bar.bottom) return { base: bar.bottom - height, height };
    if (declaredPositive && deltaNegative && bar.top < bar.bottom) return { base: bar.bottom, height };
    return { base, height };
  };
  const geometries = bars.map(geometryFor);

  const colorFor = (bar: WaterfallBarLike): string => {
    const type = (bar.type || '').toUpperCase();
    if (type === 'TOTAL' || type === 'START') return totalColor;
    if (bar.colorSign === 'POSITIVE') return positiveColor;
    if (bar.colorSign === 'NEGATIVE') return negativeColor;
    return bar.delta >= 0 ? positiveColor : negativeColor;
  };
  // Label text only — never the bridge math. "Direction" (colorSign) decides
  // whether a "-" prefix shows, exactly like it decides the bar's color; a
  // bar with no explicit direction falls back to its own delta's real sign.
  const displayText = (bar: WaterfallBarLike): string => {
    const isNegative = bar.colorSign === 'NEGATIVE' || (!bar.colorSign && bar.delta < 0);
    return `${isNegative ? '-' : ''}${formatNumber(Math.abs(bar.delta), effectiveFormat, decimals)}`;
  };

  // ── Tooltip (Tooltip accordion): enabled/trigger/pointer + styling. Style
  // props are only set when present in the format so tiles saved before these
  // controls existed keep the ECharts theme defaults.
  const tooltipTrigger = String(format?.['tooltipTrigger'] ?? 'axis');
  const tooltipEnabled = format?.['tooltipEnabled'] !== false && tooltipTrigger !== 'none';
  const axisPointerStyle = String(format?.['tooltipAxisPointer'] ?? 'shadow');
  const tooltipStyle: Record<string, unknown> = {};
  if (format?.['tooltipBackground']) tooltipStyle['backgroundColor'] = format['tooltipBackground'];
  if (format?.['tooltipBorderColor']) tooltipStyle['borderColor'] = format['tooltipBorderColor'];
  const tooltipTextStyle: Record<string, unknown> = {};
  if (format?.['tooltipTextColor']) tooltipTextStyle['color'] = format['tooltipTextColor'];
  const tooltipFontSize = Number(format?.['tooltipFontSize']);
  if (Number.isFinite(tooltipFontSize) && tooltipFontSize > 0) tooltipTextStyle['fontSize'] = tooltipFontSize;
  if (Object.keys(tooltipTextStyle).length) tooltipStyle['textStyle'] = tooltipTextStyle;
  const tooltipBorderRadius = Number(format?.['tooltipBorderRadius']);
  if (Number.isFinite(tooltipBorderRadius)) tooltipStyle['extraCssText'] = `border-radius:${tooltipBorderRadius}px;overflow:hidden;`;

  // ── Legend (Legend accordion): opt-in — only when showLegend is explicitly
  // true, so tiles saved without the key look unchanged. The bridge draws ONE
  // Delta series, so the legend entries come from three data-less marker series
  // (Increase/Decrease/Total) that exist purely for the legend items + swatches.
  const legendPosition = String(format?.['legendPosition'] ?? 'bottom');
  const showLegend = format?.['showLegend'] === true && legendPosition !== 'none';
  const legendNames = ['Increase', 'Decrease', 'Total'];
  const legendMarkerSeries = showLegend
    ? legendNames.map((name, index) => ({
        name,
        type: 'bar',
        stack: 'bridge',
        data: [],
        itemStyle: { color: [positiveColor, negativeColor, totalColor][index] },
      }))
    : [];

  // ── Axes (Axis accordion): optional titles + grid-line toggle.
  const xAxisTitle = String(format?.['xAxisLabel'] ?? '').trim();
  const yAxisTitle = String(format?.['yAxisLabel'] ?? '').trim();
  const showGridLines = format?.['showGridLines'] !== false;

  // ── Advanced accordion: bar width % + animation toggle.
  const barWidthPercent = Number(format?.['barWidthPercent']);
  const barWidth = Number.isFinite(barWidthPercent) && barWidthPercent > 0
    ? `${Math.min(100, Math.max(5, barWidthPercent))}%`
    : undefined;
  const animation = format?.['animation'] !== false;

  // ── Interaction accordion: zoom/pan via ECharts dataZoom. 'category' zooms the
  // step axis (x), 'value' the amount axis (y), 'both' adds both; each zoomed
  // axis gets an inside (wheel/drag) zoom plus an optional slider.
  const enableZoom = format?.['enableZoom'] === true;
  const zoomAxisChoice = String(format?.['zoomAxis'] ?? 'category');
  const showZoomSlider = format?.['showZoomSlider'] !== false;
  const zoomTargets = enableZoom
    ? zoomAxisChoice === 'both' ? ['x', 'y'] : zoomAxisChoice === 'value' ? ['y'] : ['x']
    : [];
  const hasCategoryZoomSlider = showZoomSlider && zoomTargets.includes('x');
  const hasValueZoomSlider = showZoomSlider && zoomTargets.includes('y');
  const dataZoom = zoomTargets.flatMap((axis) => {
    const axisIndex = axis === 'x' ? { xAxisIndex: 0 } : { yAxisIndex: 0 };
    const zooms: Record<string, unknown>[] = [{ type: 'inside', ...axisIndex }];
    if (showZoomSlider) {
      // A bottom legend occupies the bottom edge — lift the x slider above it.
      const legendAtBottom = showLegend && legendPosition !== 'top' && legendPosition !== 'right';
      zooms.push(
        axis === 'x'
          ? { type: 'slider', ...axisIndex, height: 18, bottom: legendAtBottom ? 28 : 6 }
          : { type: 'slider', ...axisIndex, width: 18, right: 6 },
      );
    }
    return zooms;
  });

  return {
    animation,
    tooltip: {
      show: tooltipEnabled,
      trigger: tooltipTrigger === 'item' ? 'item' : 'axis',
      ...(axisPointerStyle !== 'none' ? { axisPointer: { type: axisPointerStyle } } : {}),
      ...tooltipStyle,
      formatter: (params: unknown) => {
        const list = Array.isArray(params) ? params : [params];
        const point = list.find((item) => (item as { seriesName?: string }).seriesName === 'Delta') as
          | { dataIndex: number }
          | undefined;
        if (!point) return '';
        const bar = bars[point.dataIndex];
        if (!bar) return '';
        return `${bar.label}<br/>${displayText(bar)}`;
      },
    },
    ...(showLegend
      ? {
          legend: {
            show: true,
            data: legendNames,
            ...(legendPosition === 'top'
              ? { top: 0 }
              : legendPosition === 'right'
                ? { orient: 'vertical', right: 0, top: 'middle' }
                : { bottom: 0 }),
          },
        }
      : {}),
    ...(dataZoom.length ? { dataZoom } : {}),
    grid: {
      left: yAxisTitle ? 76 : 56,
      right: (showLegend && legendPosition === 'right' ? 120 : 24) + (hasValueZoomSlider ? 32 : 0),
      top: showLegend && legendPosition === 'top' ? 60 : 32,
      bottom:
        (showLegend && legendPosition !== 'top' && legendPosition !== 'right' ? 100 : 72) +
        (xAxisTitle ? 18 : 0) +
        (hasCategoryZoomSlider ? 32 : 0),
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: categories,
      ...(xAxisTitle ? { name: xAxisTitle, nameLocation: 'middle', nameGap: 52 } : {}),
      // Pin the axis line (and its category labels) to the BOTTOM of the plot
      // rather than to y=0. ECharts' default (`onZero: true`) draws it through
      // the zero gridline, so as soon as a bridge dips negative the axis cuts
      // straight through the chart and the category names sit mid-plot.
      axisLine: { onZero: false },
      axisLabel: { rotate: xAxisLabelRotation, interval: 0, formatter: wrapLabel },
    },
    yAxis: {
      type: 'value',
      ...(yAxisTitle ? { name: yAxisTitle, nameLocation: 'middle', nameGap: 52 } : {}),
      splitLine: { show: showGridLines },
      // Apply the same number formatting to the axis ticks as the bar labels.
      axisLabel: { formatter: (value: number) => formatNumber(value, effectiveFormat, decimals) },
    },
    series: [
      ...legendMarkerSeries,
      {
        name: 'Base',
        type: 'bar',
        stack: 'bridge',
        stackStrategy: 'all',
        silent: true,
        itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        emphasis: {
          disabled: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        },
        // Keep transparency on every data item as well as the series. ECharts
        // interaction/state patches can replace an item's itemStyle; retaining
        // the colour here prevents the invisible running-balance spacer from
        // becoming a visible bar down to the zero axis.
        data: geometries.map(({ base }) => ({
          value: base,
          itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        })),
      },
      {
        name: 'Delta',
        type: 'bar',
        stack: 'bridge',
        stackStrategy: 'all',
        ...(barWidth ? { barWidth } : {}),
        label: {
          show: showValues,
          position: 'top',
          formatter: (params: { dataIndex: number }) => {
            const bar = bars[params.dataIndex];
            return bar ? displayText(bar) : '';
          },
        },
        data: bars.map((bar, index) => ({
          value: geometries[index].height,
          itemStyle: { color: colorFor(bar) },
        })),
      },
    ],
  };
}

function toKpiNumberFormat(format?: Record<string, unknown>): string {
  const selected = String(format?.['numberFormat'] ?? 'NONE');
  if (selected === 'THOUSANDS') return 'thousands';
  if (selected === 'PERCENT') return 'percent';
  return 'plain';
}

function toNumberScale(value: unknown): string {
  switch (String(value ?? 'NONE')) {
    case 'COMPACT': return 'compact';
    case 'SCALE_THOUSANDS': return 'thousands';
    case 'MILLIONS': return 'millions';
    case 'BILLIONS': return 'billions';
    default: return 'none';
  }
}

function applySharedFormatFields(data: Record<string, unknown>, format?: Record<string, unknown>): Record<string, unknown> {
  if (!format) return data;
  return {
    ...data,
    title: (format['title'] as string) || data['title'],
    numberFormat: toKpiNumberFormat(format),
    numberScale: toNumberScale(format['numberFormat']),
    decimals: Number(format['decimals'] ?? 1),
    // Free-form text wrapped around the formatted value (e.g. prefix "-",
    // "Δ ", "$" / suffix "%", "#", "&"). KPI-only today, but harmless to
    // carry through for other shared-format consumers (they simply don't
    // render it).
    prefix: (format['prefix'] as string) ?? '',
    suffix: (format['suffix'] as string) ?? '',
  };
}

/** Union of measure keys across every node's `measures`, first-seen order. */
function collectMeasureKeys(nodes: Array<Record<string, unknown>>): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const walk = (list: Array<Record<string, unknown>>) => {
    (list || []).forEach((node) => {
      const measures = (node['measures'] as Record<string, unknown>) || {};
      Object.keys(measures).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      });
      if (Array.isArray(node['children'])) walk(node['children'] as Array<Record<string, unknown>>);
    });
  };
  walk(nodes);
  return keys;
}

/** Deep-copy the tree, lifting each node's `measures` entries to flat top-level
 *  keys so the drill-down table can read them with a plain per-column prop. */
function flattenNodeMeasures(nodes: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  return (nodes || []).map((node) => {
    const measures = (node['measures'] as Record<string, unknown>) || {};
    const rawChildren = Array.isArray(node['children']) ? (node['children'] as Array<Record<string, unknown>>) : [];
    const children = flattenNodeMeasures(rawChildren);
    return {
      ...node,
      ...measures,
      children,
      expanded: (node['expanded'] as boolean | undefined) ?? children.length > 0,
    };
  });
}

/** Build the DrillDownComponent `data` input from a compute response. */
export function buildDrillDownWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; fallbackHeaderName?: string; format?: Record<string, unknown> },
): Record<string, unknown> {
  const tree = (response?.['data'] ?? response?.['Data'] ?? []) as Array<Record<string, unknown>>;
  const flattenedTree = flattenNodeMeasures(tree);
  const measureKeys = collectMeasureKeys(tree);

  const treeHeader = {
    name: (tree[0]?.['levelLabel'] as string) || opts.fallbackHeaderName || 'Name',
    prop: 'label',
    expandabile: true,
    indentByLevel: true,
    width: 260,
  };
  const measureHeaders = measureKeys.map((key) => ({ name: key, prop: key, type: 'number', width: 130 }));

  const decimalsRaw = opts.format?.['decimals'];
  const decimals = decimalsRaw !== undefined && decimalsRaw !== null && decimalsRaw !== '' ? Number(decimalsRaw) : undefined;
  return {
    staticData: { data: flattenedTree, total: response?.['total'] ?? response?.['Total'] ?? null },
    nameProp: 'label',
    widgetId: opts.widgetId,
    columnMode: 'force',
    tableWidth: '100%',
    headers: [treeHeader, ...measureHeaders],
    // Round-off precision for measure cells + the Total row (Format popup's
    // `decimals`); DrillDownComponent falls back to whole numbers when absent.
    ...(decimals !== undefined && Number.isFinite(decimals) ? { decimals } : {}),
  };
}

/** Pull the `horizontalBar` array out of either envelope shape. */
function extractHorizontalBarRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const bar = dataArr?.[0]?.['horizontalBar'];
  return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
}

/** True when a BAR tile's value comes from a backend formula rather than a
 *  dataset column — an ordinary bar renders client-side and must be left alone. */
export function barPayloadHasValueFormula(payload: Record<string, unknown> | undefined): boolean {
  const value = (payload?.['value'] as Record<string, unknown> | undefined) ?? {};
  if (String(value['formula'] ?? '').trim()) return true;
  const groupFormulas = value['groupFormulas'] as Record<string, unknown> | undefined;
  return !!groupFormulas && Object.values(groupFormulas).some((formula) => String(formula ?? '').trim());
}

/** Rows for a BAR chart computed server-side: `data[0].bar`, optionally wrapped
 *  in `responseList`. Mirrors extractHorizontalBarRows, keyed `bar`. */
export function extractBarComputeRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const bar = dataArr?.[0]?.['bar'];
  return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
}

/** Build the bar chart's `data` input from a compute response. The backend
 *  returns pre-aggregated rows keyed `{ <groupBy>, value }`, so `category` and
 *  `measures` are remapped onto those keys; other capabilities pass through. */
export function buildBarComputeWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; capabilities?: Record<string, unknown>; payload?: Record<string, unknown>; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractBarComputeRows(response?.['responseList']) ??
    extractBarComputeRows(response) ??
    [];

  const payload = opts.payload ?? {};
  const groupBy = (payload['groupBy'] as Record<string, unknown> | undefined) ?? {};
  const valueBlock = (payload['value'] as Record<string, unknown> | undefined) ?? {};
  const caps = opts.capabilities ?? {};
  const capsMeasures = Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>) : [];
  const firstRow = rows[0] ?? {};
  const rowKeys = Object.keys(firstRow);

  // Key must EXIST on the rows: a missing one silently buckets every row under
  // '' and draws a single bar holding their sum. Backend renames it `department`.
  // Plain (non-formula) bar payloads carry no groupBy/value blocks — fall back
  // to the tile's capabilities for the configured column + labels.
  const configuredCategory = String(groupBy['field'] ?? '') || String(caps['category'] ?? '');
  const categoryKey =
    (configuredCategory && rowKeys.includes(configuredCategory) ? configuredCategory : '')
    || (rowKeys.includes('department') ? 'department' : '')
    || rowKeys.find((key) => key !== 'value' && typeof firstRow[key] !== 'number')
    || rowKeys.find((key) => key !== 'value')
    || configuredCategory
    || 'department';

  const valueKey =
    (rowKeys.includes('value') ? 'value' : '')
    || rowKeys.find((key) => key !== categoryKey && typeof firstRow[key] === 'number')
    || 'value';

  const valueLabel = String(valueBlock['label'] ?? '') || String(capsMeasures[0]?.['label'] ?? '') || 'Value';

  const capabilities = {
    ...(opts.capabilities ?? {}),
    category: categoryKey,
    categoryLabel: String(groupBy['label'] ?? '') || String(caps['categoryLabel'] ?? '') || configuredCategory || categoryKey,
    measures: [{ field: valueKey, label: valueLabel }],
    // Cross-filter publishes a column other tiles filter their RAW rows by, so
    // it keeps the real column, not the remapped `category`.
    ...(configuredCategory && configuredCategory !== categoryKey
      ? { crossFilterKey: (opts.capabilities?.['crossFilterKey'] as string | undefined) || configuredCategory }
      : {}),
    // Both meaningless on pre-aggregated rows.
    series: undefined,
    computedColumns: undefined,
  };

  return {
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    capabilities,
    staticData: rows,
    height: '100%',
  };
}

/** Rows for a LINE_AREA chart computed server-side: `data[0].lineArea`,
 *  optionally wrapped in `responseList`. Mirrors extractBarComputeRows. */
export function extractLineAreaComputeRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const rows = dataArr?.[0]?.['lineArea'];
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : null;
}

/** Build the line-area chart's `data` input from a compute response. The
 *  backend returns pre-aggregated rows keyed `{ <groupBy>|department, value }`
 *  — the configured `category`/`measures` fields don't exist on those rows, so
 *  they're remapped onto the actual row keys (mirrors buildBarComputeWidgetData)
 *  while the variant + formatting capabilities pass through unchanged. */
export function buildLineAreaComputeWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; capabilities?: Record<string, unknown>; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractLineAreaComputeRows(response?.['responseList']) ??
    extractLineAreaComputeRows(response) ??
    [];
  const caps = opts.capabilities ?? {};
  const firstRow = rows[0] ?? {};
  const rowKeys = Object.keys(firstRow);
  const configuredCategory = String(caps['category'] ?? '');
  const categoryKey =
    (configuredCategory && rowKeys.includes(configuredCategory) ? configuredCategory : '')
    || (rowKeys.includes('department') ? 'department' : '')
    || rowKeys.find((key) => key !== 'value' && typeof firstRow[key] !== 'number')
    || rowKeys.find((key) => key !== 'value')
    || configuredCategory
    || 'department';
  const valueKey =
    (rowKeys.includes('value') ? 'value' : '')
    || rowKeys.find((key) => key !== categoryKey && typeof firstRow[key] === 'number')
    || 'value';
  const firstMeasure = (Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>)[0] : undefined);
  const capabilities = {
    ...caps,
    category: categoryKey,
    categoryLabel: String(caps['categoryLabel'] ?? '') || configuredCategory || categoryKey,
    measures: [{
      field: valueKey,
      label: String(firstMeasure?.['label'] ?? '') || 'Value',
      renderAs: (firstMeasure?.['renderAs'] as string) || 'line',
      axis: 'y',
    }],
    // Cross-filter publishes a column other tiles filter their RAW rows by, so
    // it keeps the real column, not the remapped `category`.
    ...(configuredCategory && configuredCategory !== categoryKey
      ? { crossFilterKey: (caps['crossFilterKey'] as string | undefined) || configuredCategory }
      : {}),
    // Both meaningless on pre-aggregated rows.
    series: undefined,
    computedColumns: undefined,
  };
  return {
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    capabilities,
    staticData: rows,
    height: '100%',
  };
}

/** Build the HorizontalBarChartComponent `data` input from a compute response. */
export function buildHbWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; yAxisName?: string; xAxisName?: string; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractHorizontalBarRows(response?.['responseList']) ??
    extractHorizontalBarRows(response) ??
    [];
  return applySharedFormatFields({
    staticData: rows,
    categoryKey: 'department',
    valueKey: 'value',
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    height: '100%',
    fullYAxisLabels: true,
    yAxisLabelWidth: 150,
    valueLabelPosition: 'inside',
    yAxisName: opts.yAxisName ?? '',
    xAxisName: opts.xAxisName ?? 'Value',
    showXAxis: true,
    color: opts.format?.['positiveColor'],
    barColor: opts.format?.['positiveColor'],
    ...(opts.format?.['showValues'] === false ? { showValues: false, valueLabelPosition: 'none' } : {}),
  }, opts.format);
}

/** Pull the KPI's single numeric value out of the compute response. */
function extractKpiValue(response: Record<string, unknown> | null, kpiField = ''): number | string | null {
  if (response == null) return null;
  const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
  const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
  const dataField = fromList ?? (response as { data?: unknown }).data ?? response;
  const row = Array.isArray(dataField) ? dataField[0] : dataField;
  if (row == null) return null;
  if (typeof row === 'number' || typeof row === 'string') return row;
  const record = row as Record<string, unknown>;
  const candidate =
    record['value'] ?? record['result'] ?? (kpiField ? record[kpiField] : undefined) ??
    Object.values(record).find((v) => typeof v === 'number');
  return (candidate as number | string | undefined) ?? null;
}

/** Build the NumberStatsComponent `data` input from a compute response. */
export function buildKpiWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; kpiField?: string; format?: Record<string, unknown> },
): Record<string, unknown> {
  const value = extractKpiValue(response, opts.kpiField);
  return applySharedFormatFields({
    title: opts.title || 'KPI',
    key: 'value',
    widgetId: opts.widgetId,
    alignment: 'center',
    data: { data: [{ value }] },
  }, opts.format);
}

/** Resolve a recompute entry's chart type. The compute API is inconsistent —
 *  a WATERFALL/HBAR/KPI result carries `chartType`, but the drill-down result
 *  comes back with `chartType: null` and only `componentType: "TABLE"`. So fall
 *  back to componentType, then to the componentId, to classify it. Returns one
 *  of the RecomputeChartType strings, or '' if it can't be classified. */
export function resolveRecomputeChartType(entry: Record<string, unknown>): string {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = String(result?.['chartType'] ?? '').toUpperCase();
  if (chartType) return chartType;
  const componentType = String(result?.['componentType'] ?? '').toUpperCase();
  if (componentType === 'TABLE') return 'DRILL_DOWN_TABLE';
  // A target that is itself an input control (slider/input box) comes back as
  // `componentType: "INPUT"` with `chartType: null` — same shape as a KPI's
  // single-value result, just a different target kind.
  if (componentType === 'INPUT') return 'INPUT';
  const id = String(result?.['componentId'] ?? entry?.['componentId'] ?? '').toUpperCase();
  if (id.includes('DRILL')) return 'DRILL_DOWN_TABLE';
  if (id.includes('HBAR') || id.includes('HORIZONTAL')) return 'HORIZONTAL_BAR_CHART';
  if (id.includes('WATERFALL')) return 'WATERFALL';
  if (id.includes('KPI')) return 'KPI';
  return '';
}

/** True when a recompute `result` entry actually carries chart data. The
 *  compute API can return placeholder entries for every target chart while only
 *  SOME are recomputed — applying an empty one blanks/loads that chart, so
 *  callers skip entries that fail this check and leave those charts untouched. */
export function recomputeEntryHasData(entry: Record<string, unknown>): boolean {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = resolveRecomputeChartType(entry);
  const data = result?.['data'];
  switch (chartType) {
    case 'WATERFALL':
    case 'DRILL_DOWN_TABLE':
      return Array.isArray(data) && data.length > 0;
    // Tables usually reach here as componentType:"TABLE" (→ DRILL_DOWN_TABLE
    // above), but a backend that labels the entry chartType:"TABLE_MATRIX"
    // fell through to the default and was silently dropped.
    case 'TABLE_MATRIX': {
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'HORIZONTAL_BAR_CHART': {
      const rows = extractHorizontalBarRows((result as { responseList?: unknown }).responseList) ?? extractHorizontalBarRows(result);
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'BAR': {
      const rows = extractBarComputeRows((result as { responseList?: unknown }).responseList) ?? extractBarComputeRows(result);
      return Array.isArray(rows) && rows.length > 0;
    }
    // Client-side rendered families: a recompute entry is only usable when it
    // actually carries replacement rows (the builder swaps them into the tile's
    // staticData). Without these cases every LINE_AREA/SCATTER_BUBBLE entry was
    // silently dropped by the default branch, so those charts never reacted to
    // input-driven recomputes at all.
    case 'LINE_AREA': {
      // Server-computed envelope (`data[0].lineArea`) OR flat replacement rows.
      const envelope = extractLineAreaComputeRows((result as { responseList?: unknown }).responseList) ?? extractLineAreaComputeRows(result);
      if (envelope) return envelope.length > 0;
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'SCATTER_BUBBLE': {
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'KPI':
    case 'INPUT': {
      const row = Array.isArray(data) ? data[0] : data;
      return row != null;
    }
    default:
      return false;
  }
}

/** Build the `data`-shaped fields for a target chart from a recompute `result`
 *  entry (`{ componentId, label, result: { chartType, data, … } }`). Returns the
 *  fields to merge onto the existing widget config, or null for unknown types.
 *  Identity fields (type/layout/height/componentId/payload) must be preserved by
 *  the caller — only the data-bearing fields are returned here. */
export function buildRecomputedWidgetFields(
  entry: Record<string, unknown>,
  ctx: { widgetId: string; title?: string; yAxisName?: string; xAxisName?: string; kpiField?: string; format?: Record<string, unknown>; payload?: Record<string, unknown>; capabilities?: Record<string, unknown> },
): Record<string, unknown> | null {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = resolveRecomputeChartType(entry);
  switch (chartType) {
    case 'WATERFALL': {
      const rawBars = (Array.isArray(result['data'])
        ? result['data']
        : ((result['data'] as { data?: WaterfallBarLike[] })?.data || [])) as WaterfallBarLike[];
      const bars = applyWaterfallStepSigns(rawBars, ctx.payload);
      return { option: buildWaterfallOption(bars, ctx.format), waterfallBars: bars };
    }
    case 'DRILL_DOWN_TABLE':
      return buildDrillDownWidgetData(result, { widgetId: ctx.widgetId, fallbackHeaderName: ctx.title, format: ctx.format });
    case 'HORIZONTAL_BAR_CHART':
      return buildHbWidgetData(result, { widgetId: ctx.widgetId, title: ctx.title, yAxisName: ctx.yAxisName, xAxisName: ctx.xAxisName, format: ctx.format });
    case 'BAR': {
      // Formula-driven bars, OR a bar already rendered from SERVER rows (its
      // capabilities.category is the remapped row key, e.g. 'department', so
      // the envelope rows fit it). An ordinary client-side bar (capabilities
      // keep the real dataset columns) is left alone — this pre-aggregated
      // response would flatten it, and the dropdown filters it locally instead.
      const barRows = extractBarComputeRows((result as { responseList?: unknown }).responseList) ?? extractBarComputeRows(result);
      const barCaps = ctx.capabilities ?? (ctx.payload?.['capabilities'] as Record<string, unknown> | undefined);
      const capCategory = String(barCaps?.['category'] ?? '');
      const serverRendered = !!barRows?.length && !!capCategory && capCategory in ((barRows[0] ?? {}) as Record<string, unknown>);
      if (!barPayloadHasValueFormula(ctx.payload) && !serverRendered) return null;
      return buildBarComputeWidgetData(result, {
        widgetId: ctx.widgetId,
        title: ctx.title,
        capabilities: barCaps,
        payload: ctx.payload,
        format: ctx.format,
      });
    }
    case 'LINE_AREA': {
      // Server-computed line-area (`data[0].lineArea` pre-aggregated rows) —
      // rebuild the widget with remapped capabilities. Flat/absent rows return
      // null so a client-side-rendered line chart is left alone.
      const laRows = extractLineAreaComputeRows((result as { responseList?: unknown }).responseList) ?? extractLineAreaComputeRows(result);
      if (!laRows?.length) return null;
      return buildLineAreaComputeWidgetData(result, {
        widgetId: ctx.widgetId,
        title: ctx.title,
        capabilities: ctx.capabilities ?? (ctx.payload?.['capabilities'] as Record<string, unknown> | undefined),
        format: ctx.format,
      });
    }
    case 'KPI':
      return buildKpiWidgetData(result, { widgetId: ctx.widgetId, title: ctx.title, kpiField: ctx.kpiField, format: ctx.format });
    case 'INPUT':
      // A target that is itself an input control just gets its live `value`
      // updated (the backend already computed it from the driving control's
      // formula) — the caller is responsible for pushing this into whatever
      // field the actual slider/input-box widget renders from.
      return { value: extractKpiValue(result) };
    default:
      return null;
  }
}


